import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

interface PagedApiResponse<T> {
  success: boolean;
  data: T;
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export interface TaxonomyGroupDto {
  id: string;
  key: string;
  name: string;
  categoryCount: number;
  sortOrder: number;
}

export interface TaxonomyCategoryDto {
  id: string;
  key: string;
  name: string;
  groupKey: string;
  groupName: string;
  description?: string | null;
  entryCount: number;
}

export interface TaxonomyCategoryDetailDto extends TaxonomyCategoryDto {
  fieldSchema: string;
}

export interface TaxonomyEntryDto {
  id: string;
  name: string;
  data: string; // JSON string
}

export interface TaxonomyEntryImageDto {
  id: string;
  mediaAssetId?: string | null;
  imageUrl?: string | null;
  caption?: string | null;
}

export interface TaxonomyEntryDetailDto {
  id: string;
  categoryKey: string;
  categoryName: string;
  name: string;
  data: string; // JSON string
  images: TaxonomyEntryImageDto[];
}

export interface TaxonomyAssociationDto {
  id: string;
  relatedEntryId: string;
  relatedEntryName: string;
  associationType: string;
  notes?: string | null;
  direction: 'incoming' | 'outgoing';
}

export interface FieldSchemaEntry {
  name: string;
  type: string;
  description: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function parseEntryData(data: string): Record<string, unknown> {
  try {
    return JSON.parse(data) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export function parseFieldSchema(schema: string): FieldSchemaEntry[] {
  try {
    return JSON.parse(schema) as FieldSchemaEntry[];
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

export function useTaxonomyGroups() {
  const { getToken } = useAuth();
  return useQuery<TaxonomyGroupDto[]>({
    queryKey: ['taxonomy-groups'],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get('api/v2/taxonomy/groups')
        .json<ApiResponse<TaxonomyGroupDto[]>>();
      return resp.data ?? [];
    },
  });
}

export function useTaxonomyCategories(groupKey?: string) {
  const { getToken } = useAuth();
  return useQuery<TaxonomyCategoryDto[]>({
    queryKey: ['taxonomy-categories', groupKey ?? ''],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const q = groupKey ? `?groupKey=${encodeURIComponent(groupKey)}` : '';
      const resp = await client
        .get(`api/v2/taxonomy/categories${q}`)
        .json<ApiResponse<TaxonomyCategoryDto[]>>();
      return resp.data ?? [];
    },
  });
}

export function useTaxonomyCategory(categoryKey: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<TaxonomyCategoryDetailDto>({
    enabled: !!categoryKey,
    queryKey: ['taxonomy-category', categoryKey],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get(`api/v2/taxonomy/categories/${categoryKey}`)
        .json<ApiResponse<TaxonomyCategoryDetailDto>>();
      return resp.data;
    },
  });
}

export interface TaxonomyEntriesResult {
  items: TaxonomyEntryDto[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export function useTaxonomyEntries(opts: {
  categoryKey?: string;
  search?: string;
  sort?: string;
  sortDesc?: boolean;
  page?: number;
  pageSize?: number;
}) {
  const { getToken } = useAuth();
  const {
    categoryKey,
    search,
    sort = 'name',
    sortDesc = false,
    page = 1,
    pageSize = 50,
  } = opts;
  return useQuery<TaxonomyEntriesResult>({
    enabled: !!categoryKey,
    queryKey: [
      'taxonomy-entries',
      categoryKey,
      search ?? '',
      sort,
      sortDesc,
      page,
      pageSize,
    ],
    // Don't retry on 404 (category not seeded yet) — just return empty
    retry: (failureCount, error) => {
      if (error && typeof error === 'object' && 'response' in error) {
        const resp = (error as { response?: { status?: number } }).response;
        if (resp?.status === 404) return false;
      }
      return failureCount < 2;
    },
    queryFn: async () => {
      const client = createApiClient(getToken);
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      params.set('sort', sort);
      if (sortDesc) params.set('sortDesc', 'true');
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      try {
        const resp = await client
          .get(`api/v2/taxonomy/categories/${categoryKey}/entries?${params}`)
          .json<PagedApiResponse<TaxonomyEntryDto[]>>();
        return {
          items: resp.data ?? [],
          total: resp.pagination?.total ?? 0,
          page: resp.pagination?.page ?? page,
          pageSize: resp.pagination?.pageSize ?? pageSize,
          totalPages: resp.pagination?.totalPages ?? 0,
        };
      } catch (err) {
        // Return empty result for missing categories (404)
        if (err && typeof err === 'object' && 'response' in err) {
          const resp = (err as { response?: { status?: number } }).response;
          if (resp?.status === 404) {
            return { items: [], total: 0, page, pageSize, totalPages: 0 };
          }
        }
        throw err;
      }
    },
  });
}

export function useTaxonomyEntry(entryId: string | null) {
  const { getToken } = useAuth();
  return useQuery<TaxonomyEntryDetailDto>({
    enabled: !!entryId,
    queryKey: ['taxonomy-entry', entryId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get(`api/v2/taxonomy/entries/${entryId}`)
        .json<ApiResponse<TaxonomyEntryDetailDto>>();
      return resp.data;
    },
  });
}

export function useTaxonomyAssociations(entryId: string | null) {
  const { getToken } = useAuth();
  return useQuery<TaxonomyAssociationDto[]>({
    enabled: !!entryId,
    queryKey: ['taxonomy-associations', entryId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get(`api/v2/taxonomy/entries/${entryId}/associations`)
        .json<ApiResponse<TaxonomyAssociationDto[]>>();
      return resp.data ?? [];
    },
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

export function useCopyToWorld() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      worldId: string;
      entryIds: string[];
      elementType?: string;
    }) => {
      const client = createApiClient(getToken);
      const resp = await client
        .post('api/v2/taxonomy/copy-to-world', { json: input })
        .json<ApiResponse<{ createdElementIds: string[] }>>();
      return resp.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['world'] });
      void qc.invalidateQueries({ queryKey: ['worlds'] });
    },
  });
}
